/**
 * Tournament Draw repository (ADR-031 P3).
 * Path: arenas/{arenaId}/events/{eventId}/draws/{drawId}
 * Draw documents keep ManagedDraw shape (incl. structure). Fixtures live in fixtures/.
 */

import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { touchEvent } from "./tournamentEventRepository";

function drawsCol(arenaId, eventId) {
  return collection(db, "arenas", arenaId, "events", eventId, "draws");
}

function drawRef(arenaId, eventId, drawId) {
  return doc(db, "arenas", arenaId, "events", eventId, "draws", drawId);
}

/**
 * @param {string} arenaId
 * @param {string} eventId
 */
export async function listDrawsForEvent(arenaId, eventId) {
  if (!arenaId || !eventId) return [];
  const snap = await getDocs(drawsCol(arenaId, eventId));
  return snap.docs.map((d) => {
    const data = d.data();
    const { fixtures: _omit, ...rest } = data;
    return {
      drawId: d.id,
      eventId,
      arenaId,
      competitionId: data.competitionId || arenaId,
      ...rest,
    };
  });
}

/**
 * @param {string} arenaId
 * @param {string[]} eventIds
 */
export async function listDrawsForArena(arenaId, eventIds) {
  if (!arenaId) return [];
  const ids = Array.isArray(eventIds) ? eventIds : [];
  const nested = await Promise.all(ids.map((eventId) => listDrawsForEvent(arenaId, eventId)));
  return nested.flat();
}

/**
 * @param {string} arenaId
 * @param {string} eventId
 * @param {string} drawId
 */
export async function fetchDraw(arenaId, eventId, drawId) {
  if (!arenaId || !eventId || !drawId) return null;
  const snap = await getDoc(drawRef(arenaId, eventId, drawId));
  if (!snap.exists()) return null;
  const data = snap.data();
  const { fixtures: _omit, ...rest } = data;
  return {
    drawId: snap.id,
    eventId,
    arenaId,
    competitionId: data.competitionId || arenaId,
    ...rest,
  };
}

/**
 * Persist draw metadata/structure without embedding fixtures array.
 * @param {string} arenaId
 * @param {string} eventId
 * @param {object} draw
 */
export async function upsertDraw(arenaId, eventId, draw) {
  const drawId = draw.drawId;
  if (!arenaId || !eventId || !drawId) {
    throw new Error("arenaId, eventId, and drawId required.");
  }
  const { fixtures: _omit, ...rest } = draw;
  await setDoc(
    drawRef(arenaId, eventId, drawId),
    {
      ...rest,
      drawId,
      eventId,
      arenaId,
      competitionId: draw.competitionId || arenaId,
      updatedAt: serverTimestamp(),
      createdAt: draw.createdAt || serverTimestamp(),
    },
    { merge: true }
  );
  await touchEvent(arenaId, eventId);
  return { ...rest, drawId, eventId, arenaId };
}

/**
 * @param {string} arenaId
 * @param {string} eventId
 * @param {string} drawId
 */
export async function deleteDraw(arenaId, eventId, drawId) {
  if (!arenaId || !eventId || !drawId) return;
  await deleteDoc(drawRef(arenaId, eventId, drawId));
  await touchEvent(arenaId, eventId);
}
